import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router';
import { getTenant, getTenantHealth, provisionTenantSchema } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { FlowisAccountProbe, TenantHealth, TenantProbe } from '../../api/types';
import { CopyableText } from '../../components/CopyableText';
import { ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { TenantAccountsTab } from './TenantAccountsTab';
import { TenantDialog } from './TenantDialog';

type TenantTab = 'settings' | 'accounts' | 'connectivity';

/**
 * A traffic light for one aspect of the tenant: green works, amber needs attention, red is broken,
 * grey is switched off on purpose — a disabled tenant or a tenant integrated over REST alone is a
 * choice, not a fault, and must not read as one.
 */
type Signal = 'ok' | 'warning' | 'error' | 'off';

const signalChipColor = { ok: 'success', warning: 'warning', error: 'error', off: 'default' } as const;

function SignalIcon({ signal, fontSize = 'medium' }: { signal: Signal; fontSize?: 'medium' | 'small' }) {
  switch (signal) {
    case 'ok':
      return <CheckCircleIcon color="success" fontSize={fontSize} />;
    case 'warning':
      return <WarningAmberIcon color="warning" fontSize={fontSize} />;
    case 'error':
      return <CancelIcon color="error" fontSize={fontSize} />;
    case 'off':
      return <RemoveCircleOutlineIcon color="disabled" fontSize={fontSize} />;
  }
}

/** One tile of the status strip: the light, what it is about, its state in a few words, and why that matters. */
function SignalTile({
  signal,
  label,
  value,
  hint,
}: {
  signal: Signal;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Box sx={{ pt: 0.25, display: 'flex' }}>
        <SignalIcon signal={signal} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {hint}
        </Typography>
      </Box>
    </Box>
  );
}

/** Label/value rows, the label column narrow and muted so the values line up and read as a column. */
function PropertyTable({ rows }: { rows: { label: string; value: React.ReactNode }[] }) {
  return (
    <Table
      size="small"
      sx={{
        '& th, & td': { px: 0, py: 1 },
        '& tr:last-child th, & tr:last-child td': { borderBottom: 0 },
      }}
    >
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.label}>
            <TableCell
              component="th"
              scope="row"
              sx={{ color: 'text.secondary', fontWeight: 400, width: '38%', pr: 2, verticalAlign: 'top' }}
            >
              {row.label}
            </TableCell>
            <TableCell>{row.value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
      {children}
    </Typography>
  );
}

/** One line of the connectivity table: the light, what was checked, where, and — when red — what went wrong. */
function CheckRow({
  ok,
  label,
  target,
  detail,
}: {
  ok: boolean;
  label: string;
  target: React.ReactNode;
  detail: string | null;
}) {
  const { t } = useTranslation();

  return (
    <TableRow hover>
      <TableCell padding="checkbox" sx={{ pl: 2 }}>
        <Box sx={{ display: 'flex' }}>
          <SignalIcon signal={ok ? 'ok' : 'error'} fontSize="small" />
        </Box>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{label}</TableCell>
      <TableCell>{target}</TableCell>
      <TableCell>
        <Typography variant="body2" color={ok ? 'success.main' : 'error.main'} sx={{ fontWeight: 500 }}>
          {ok ? t('admin.tenantDetail.health.ok') : t('admin.tenantDetail.health.failed')}
        </Typography>
        {detail !== null && (
          <Typography variant="caption" color="error" display="block">
            {detail}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

function ProbeRow({ label, probe }: { label: string; probe: TenantProbe | null }) {
  if (probe === null) return null;

  return (
    <CheckRow
      ok={probe.ok}
      label={label}
      target={
        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
          {probe.target}
        </Typography>
      }
      detail={probe.detail}
    />
  );
}

/** One Flowis account, asked through its own credential: the path a send takes, so a red row is the send's answer. */
function FlowisAccountRow({ account }: { account: FlowisAccountProbe }) {
  const { t } = useTranslation();

  return (
    <CheckRow
      ok={account.ok}
      label={t('admin.tenantDetail.health.flowisAccount')}
      target={
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2">{account.name}</Typography>
          {account.participantId !== null && (
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {account.participantId}
            </Typography>
          )}
          {!account.enabled && (
            <Typography variant="body2" color="text.secondary">
              {t('admin.tenantDetail.health.accountDisabledSuffix')}
            </Typography>
          )}
        </Box>
      }
      detail={account.detail}
    />
  );
}

function failedChecks(health: TenantHealth): number {
  const probes = [health.queueIn, health.queueOut, health.container, health.fileStore];
  return (
    probes.filter((probe) => probe !== null && !probe.ok).length +
    health.flowisAccounts.filter((account) => !account.ok).length
  );
}

/**
 * Live checks against everything the tenant's work passes through — the Service Bus queues, the
 * inbox container, the bridge's own file store and Flowis through each account — asked for on open
 * and on the refresh button, never on a poll, because every ask costs the dependencies a call.
 */
function ConnectivityCard({ tenantGid }: { tenantGid: string }) {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: queryKeys.admin.tenantHealth(tenantGid),
    queryFn: ({ signal }) => getTenantHealth(tenantGid, signal),
    // Never re-probed behind the administrator's back; the refresh button is the way to ask again.
    staleTime: Infinity,
    retry: false,
  });

  const health = query.data;
  const failed = health === undefined ? 0 : failedChecks(health);
  const verdict: Signal = failed === 0 ? 'ok' : 'error';

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{t('admin.tenantDetail.health.title')}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          {health !== undefined && !query.isFetching && (
            <Chip
              size="small"
              variant="outlined"
              color={signalChipColor[verdict]}
              icon={<SignalIcon signal={verdict} fontSize="small" />}
              label={
                failed === 0
                  ? t('admin.tenantDetail.health.allOk')
                  : t('admin.tenantDetail.health.someFailed', { count: failed })
              }
            />
          )}
          <Tooltip title={t('admin.tenantDetail.health.refresh')}>
            <span>
              <IconButton size="small" disabled={query.isFetching} onClick={() => void query.refetch()}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.tenantDetail.health.description')}
        </Typography>

        {query.isPending || query.isFetching ? (
          <LoadingBlock label={t('admin.tenantDetail.health.checking')} />
        ) : health === undefined ? (
          <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <Stack spacing={2}>
            {/* The bus half: absent altogether when no namespace is configured. */}
            {!health.serviceBusConfigured && (
              <Alert severity="info">{t('admin.tenantDetail.health.notConfigured')}</Alert>
            )}
            {health.serviceBusConfigured && !health.consumerEnabled && (
              <Alert severity="warning">{t('admin.tenantDetail.health.consumerOff')}</Alert>
            )}
            {health.serviceBusConfigured && health.consumerEnabled && !health.useQueues && (
              <Alert severity="info">{t('admin.tenantDetail.health.busOff')}</Alert>
            )}

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t('admin.tenantDetail.health.check')}</TableCell>
                    <TableCell>{t('admin.tenantDetail.health.target')}</TableCell>
                    <TableCell>{t('admin.tenantDetail.health.result')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <ProbeRow label={t('admin.tenants.dialog.queueInName')} probe={health.queueIn} />
                  <ProbeRow label={t('admin.tenants.dialog.queueOutName')} probe={health.queueOut} />
                  <ProbeRow label={t('admin.tenants.dialog.containerName')} probe={health.container} />
                  {/* Everything else a submission passes through — the same for REST callers, so
                      probed whatever the bus configuration says. */}
                  <ProbeRow label={t('admin.tenantDetail.health.fileStore')} probe={health.fileStore} />
                  {health.flowisAccounts.map((account) => (
                    <FlowisAccountRow key={account.accountGid} account={account} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {health.flowisAccounts.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('admin.tenantDetail.health.noAccounts')}
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export function TenantDetailPage() {
  const { tenantGid = '' } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const [tab, setTab] = useState<TenantTab>('settings');
  const [dialogOpen, setDialogOpen] = useState(false);

  const query = useQuery({
    queryKey: queryKeys.admin.tenant(tenantGid),
    queryFn: ({ signal }) => getTenant(tenantGid, signal),
    enabled: tenantGid.length > 0,
  });

  const provisionMutation = useMutation({
    mutationFn: () => provisionTenantSchema(tenantGid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
      toastSuccess(t('admin.tenantDetail.schemaProvisioned'));
    },
    onError: toastError,
  });

  if (query.isPending) return <LoadingBlock label={t('admin.tenantDetail.loading')} />;
  if (query.isError) return <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />;

  const tenant = query.data;
  const schemaState = {
    Ready: {
      signal: 'ok' as const,
      label: t('admin.tenantDetail.schemaReady'),
      hint: t('admin.tenantDetail.schemaReadyHint'),
    },
    Missing: {
      signal: 'error' as const,
      label: t('admin.tenantDetail.schemaMissing'),
      hint: t('admin.tenantDetail.schemaMissingHint'),
    },
    Outdated: {
      signal: 'warning' as const,
      label: t('admin.tenantDetail.schemaOutdated'),
      hint: t('admin.tenantDetail.schemaOutdatedHint'),
    },
  }[tenant.schemaState];

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button component={RouterLink} to="/admin" startIcon={<ArrowBackIcon />}>
          {t('admin.tabs.tenants')}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={
            provisionMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />
          }
          variant="outlined"
          disabled={provisionMutation.isPending}
          onClick={() => provisionMutation.mutate()}
        >
          {provisionMutation.isPending
            ? t('admin.tenantDetail.provisioningSchema')
            : t('admin.tenantDetail.provisionSchema')}
        </Button>
        <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setDialogOpen(true)}>
          {t('admin.tenantDetail.edit')}
        </Button>
      </Box>

      <Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5">{tenant.name}</Typography>
          <Chip
            size="small"
            variant="outlined"
            color={tenant.enabled ? 'success' : 'default'}
            label={tenant.enabled ? t('states.enabled') : t('states.disabled')}
          />
          <Chip
            size="small"
            variant="outlined"
            color={signalChipColor[schemaState.signal]}
            label={schemaState.label}
          />
          {tenant.testMode && (
            <Chip size="small" color="warning" label={t('admin.tenantDetail.testModeChip')} />
          )}
        </Stack>
        {tenant.testMode && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('admin.tenantDetail.testModeAlert')}
          </Alert>
        )}
      </Box>

      <Tabs value={tab} onChange={(_event, value: TenantTab) => setTab(value)}>
        <Tab value="settings" label={t('admin.tenantDetail.settingsTab')} />
        <Tab
          value="accounts"
          label={t('admin.tenantDetail.accountsTab', { count: tenant.accounts.length })}
        />
        {/* Its own tab, so the live checks — every one a call to a dependency — run only when an
            administrator actually comes to look at them. */}
        <Tab value="connectivity" label={t('admin.tenantDetail.connectivityTab')} />
      </Tabs>

      {tab === 'settings' && (
        <Stack spacing={2}>
          {/* The status strip: four lights an administrator reads at a glance before anything else. */}
          <Card>
            <CardContent>
              <SectionTitle>{t('admin.tenantDetail.statusSection')}</SectionTitle>
              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
                }}
              >
                <SignalTile
                  signal={tenant.enabled ? 'ok' : 'off'}
                  label={t('columns.state')}
                  value={tenant.enabled ? t('states.enabled') : t('states.disabled')}
                  hint={
                    tenant.enabled
                      ? t('admin.tenantDetail.enabledHint')
                      : t('admin.tenantDetail.disabledHint')
                  }
                />
                <SignalTile
                  signal={schemaState.signal}
                  label={t('admin.tenantDetail.schemaState')}
                  value={schemaState.label}
                  hint={schemaState.hint}
                />
                <SignalTile
                  signal={tenant.useQueues ? 'ok' : 'off'}
                  label={t('admin.tenantDetail.queues')}
                  value={
                    tenant.useQueues ? t('admin.tenantDetail.queuesOn') : t('admin.tenantDetail.queuesOff')
                  }
                  hint={
                    tenant.useQueues
                      ? t('admin.tenantDetail.queuesOnHint')
                      : t('admin.tenantDetail.queuesOffHint')
                  }
                />
                <SignalTile
                  signal={tenant.testMode ? 'warning' : 'ok'}
                  label={t('admin.tenantDetail.testMode')}
                  value={
                    tenant.testMode ? t('admin.tenantDetail.testModeOn') : t('admin.tenantDetail.testModeOff')
                  }
                  hint={
                    tenant.testMode
                      ? t('admin.tenantDetail.testModeOnHint')
                      : t('admin.tenantDetail.testModeOffHint')
                  }
                />
              </Box>
            </CardContent>
          </Card>

          {/* Two columns of label/value rows: who the tenant is, and the resources its messages travel through. */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <Card>
              <CardContent>
                <SectionTitle>{t('admin.tenantDetail.identitySection')}</SectionTitle>
                <PropertyTable
                  rows={[
                    {
                      label: t('admin.tenantDetail.identifier'),
                      value: <CopyableText value={tenant.tenantGid} />,
                    },
                    { label: t('admin.tenants.code'), value: <CopyableText value={tenant.code} /> },
                    {
                      label: t('admin.tenantDetail.tenantRole'),
                      value: <CopyableText value={`tenant.${tenant.code}`} />,
                    },
                  ]}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  {t('admin.tenantDetail.rolesNote')}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <SectionTitle>{t('admin.tenantDetail.channelsSection')}</SectionTitle>
                <PropertyTable
                  rows={[
                    {
                      label: t('admin.tenants.dialog.containerName'),
                      value: <CopyableText value={tenant.containerName} />,
                    },
                    {
                      label: t('admin.tenants.dialog.queueInName'),
                      value: <CopyableText value={tenant.queueInName} />,
                    },
                    {
                      label: t('admin.tenants.dialog.queueOutName'),
                      value: <CopyableText value={tenant.queueOutName} />,
                    },
                  ]}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  {t('admin.tenantDetail.channelsNote')}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Typography variant="caption" color="text.secondary">
            {t('admin.tenantDetail.noDelete')}
          </Typography>
        </Stack>
      )}

      {tab === 'connectivity' && <ConnectivityCard tenantGid={tenant.tenantGid} />}

      {tab === 'accounts' && <TenantAccountsTab tenantGid={tenant.tenantGid} accounts={tenant.accounts} />}

      {dialogOpen && <TenantDialog tenant={tenant} onClose={() => setDialogOpen(false)} />}
    </Stack>
  );
}
